import {
  pgTable,
  text,
  serial,
  integer,
  timestamp,
  jsonb,
  varchar,
  pgEnum,
  index,
} from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";
import { agencies, clients } from "./business";

// ─── Enums ────────────────────────────────────────────────────────────────────

export const sourcePlatformEnum = pgEnum("migration_source_platform", [
  "ease",
  "emp_nav",
  "zywave",
  "bernie_portal",
  "generic",
  "manual",
]);

export const migrationStepStatusEnum = pgEnum("migration_step_status", [
  "pending",
  "uploaded",
  "previewing",
  "confirmed",
  "skipped",
  "error",
]);

export const migrationJobStatusEnum = pgEnum("migration_job_status", [
  "not_started",
  "in_progress",
  "complete",
]);

export const SOURCE_PLATFORMS = sourcePlatformEnum.enumValues;
export type SourcePlatform = (typeof SOURCE_PLATFORMS)[number];

export const MIGRATION_STEP_STATUS = migrationStepStatusEnum.enumValues;
export type MigrationStepStatus = (typeof MIGRATION_STEP_STATUS)[number];

export const MIGRATION_JOB_STATUS = migrationJobStatusEnum.enumValues;
export type MigrationJobStatus = (typeof MIGRATION_JOB_STATUS)[number];

// ─── Phase 1: bulk client import batch ───────────────────────────────────────
// Broker uploads a companies list from source platform; rows stay in rawData
// (jsonb) until broker confirms, then spawn one migrationJobs row per company.

export const migrationBatches = pgTable(
  "migration_batches",
  {
    id: serial("id").primaryKey(),
    agencyId: varchar("agency_id")
      .notNull()
      .references(() => agencies.id),
    sourcePlatform: sourcePlatformEnum("source_platform")
      .notNull()
      .default("generic"),
    status: migrationStepStatusEnum("status").notNull().default("pending"),
    totalRows: integer("total_rows"),
    confirmedCount: integer("confirmed_count"),
    rawData: jsonb("raw_data"), // parsed rows before confirmation
    columnMapping: jsonb("column_mapping"), // broker's header → fieldKey map
    validationErrors: jsonb("validation_errors"),
    fileName: text("file_name"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (t) => ({
    agencyCreatedIdx: index("migration_batches_agency_created_idx").on(
      t.agencyId,
      t.createdAt,
    ),
  }),
);

export const insertMigrationBatchSchema = createInsertSchema(
  migrationBatches,
).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertMigrationBatch = z.infer<typeof insertMigrationBatchSchema>;
export type MigrationBatch = typeof migrationBatches.$inferSelect;

// ─── Phase 2: per-client migration job (wizard state) ────────────────────────
// One row per client being migrated. Each step (census/plans/enrollments) has
// its own status + jsonb blobs for staging before the broker confirms.

export const migrationJobs = pgTable(
  "migration_jobs",
  {
    id: serial("id").primaryKey(),
    agencyId: varchar("agency_id")
      .notNull()
      .references(() => agencies.id),
    clientId: integer("client_id").references(() => clients.id, {
      onDelete: "set null",
    }),
    batchId: integer("batch_id").references(() => migrationBatches.id, {
      onDelete: "set null",
    }),
    clientName: text("client_name").notNull(),
    sourcePlatform: sourcePlatformEnum("source_platform")
      .notNull()
      .default("generic"),
    importBatchId: varchar("import_batch_id"), // UUID tag on imported records for traceability
    status: migrationJobStatusEnum("status").notNull().default("not_started"),
    currentStep: text("current_step").notNull().default("roster"), // roster | census | plans | enrollments | complete

    // Roster step (Phase 2 added later) — full employee census from Ease.
    // Skippable when the broker has already loaded employees via the
    // standalone Employee Import flow or a previous wizard run.
    rosterStatus: migrationStepStatusEnum("roster_status")
      .notNull()
      .default("pending"),
    rosterRawData: jsonb("roster_raw_data"),
    rosterData: jsonb("roster_data"),
    rosterValidationErrors: jsonb("roster_validation_errors"),
    rosterFileName: text("roster_file_name"),
    rosterColumnMapping: jsonb("roster_column_mapping"),

    // Census step
    censusStatus: migrationStepStatusEnum("census_status")
      .notNull()
      .default("pending"),
    censusRawData: jsonb("census_raw_data"),
    censusData: jsonb("census_data"),
    censusValidationErrors: jsonb("census_validation_errors"),
    censusFileName: text("census_file_name"),
    censusColumnMapping: jsonb("census_column_mapping"),

    // Plans step
    plansStatus: migrationStepStatusEnum("plans_status")
      .notNull()
      .default("pending"),
    plansData: jsonb("plans_data"),
    plansValidationErrors: jsonb("plans_validation_errors"),

    // Enrollments step
    enrollmentsStatus: migrationStepStatusEnum("enrollments_status")
      .notNull()
      .default("pending"),
    enrollmentsData: jsonb("enrollments_data"),
    enrollmentsValidationErrors: jsonb("enrollments_validation_errors"),
    enrollmentsFileName: text("enrollments_file_name"),

    // Audit
    acknowledgedExceptions: jsonb("acknowledged_exceptions"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (t) => ({
    agencyStatusIdx: index("migration_jobs_agency_status_idx").on(
      t.agencyId,
      t.status,
    ),
    clientIdx: index("migration_jobs_client_idx").on(t.clientId),
    agencyCreatedIdx: index("migration_jobs_agency_created_idx").on(
      t.agencyId,
      t.createdAt,
    ),
  }),
);

export const insertMigrationJobSchema = createInsertSchema(migrationJobs).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertMigrationJob = z.infer<typeof insertMigrationJobSchema>;
export type MigrationJob = typeof migrationJobs.$inferSelect;
